/* global G */
/* eslint-disable object-curly-newline,no-undefined,no-unused-expressions,no-unused-vars */
import { Box, InputBase, Menu, MenuItem, useTheme } from '@mui/material'
import ApplicationContext from '@platform/react/context/application'
import { useEventCallback, useEventHandler, useMemoRef, useStyles } from '@platform/react/hook'
import { arrayOf } from 'lib/util'
import PlatformEvent from 'lib/util/event'
import { cloneElement, useContext, useEffect, useMemo, useRef, useState, useTransition } from 'react'
import Icon from 'ui/Element/Icon'
import Text from 'ui/Element/Text'
import Chip from 'ui/Element/Text/Chip'
import FabButton from 'ui/Element/Button/Fab'

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '0.75rem',
    borderTop: `0.0625rem solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flex: '0 0 auto',
  },
  attachments: {
    width: '100%',
  },
  editBanner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    padding: '0.375rem 0.5rem',
    marginBottom: '0.75rem',
    borderRadius: '0.5rem',
    backgroundColor: theme.palette.gray[960],
    borderLeft: `0.1875rem solid ${theme.palette.pending[700]}`,
  },
  editBannerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.5rem',
    flex: 1,
    minWidth: 0,
  },
  editPreview: {
    flex: 1,
    minWidth: 0,
    fontStyle: 'italic',
  },
  editCancel: {
    display: 'flex',
    cursor: 'pointer',
    flexShrink: 0,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '0.75rem',
    maxHeight: '12rem',
    // overflowY: 'scroll',
    marginBottom: '0.75rem',
  },
  field: {
    padding: 0,
    flex: 1,
    maxHeight: 'inherit',
    overflowY: 'scroll',
  },
  input: {
    padding: 0,
    fontSize: '1rem',
    lineHeight: 1.4,
  },
  clear: {
    display: 'flex',
    cursor: 'pointer',
    alignSelf: 'center',
    paddingBottom: '0.125rem',
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  left: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.75rem',
  },
  chipWrapper: {
    display: 'inline-flex',
  },
  menuPaper: {
    borderRadius: '0.75rem',
    boxShadow: '0 0.25rem 1.5rem rgba(0, 0, 0, 0.16)',
    marginTop: '-0.75rem',
    minWidth: '18rem',
  },
  menuList: {
    padding: '0.5rem',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    borderRadius: '0.5rem',
    padding: '0.5rem 0.75rem',
  },
  ...theme.custom.messageField,
})

/**
 * MessageField ui element.
 *
 * Renders the message compose field: attachment previews on top, a text input, and an actions row
 * holding the message-type chip, the attach (paperclip) button and the send button. Attachments
 * are handled inline (the paperclip opens the picker via the nested upload child) — like mobile.
 *
 * @param {Object} forwardedRef      a reference to the root element
 * @param {String} group             the attachment group to watch
 * @param {Object} value             the value of the field
 * @param {Object} value.text        the current value of the input field
 * @param {boolean} [edit]           whether the field is in edit mode
 * @param {string} [editLabel]       the label shown in the edit-mode banner
 * @param {Object} events            the element's event handlers
 * @param {function} events.onOpen   obtains the available message types
 * @param {function} events.onChange called whenever the text changes
 * @param {function} events.onSend   called whenever enter or the send button are pressed
 * @param {function} events.onAttachment called whenever files are pasted into the field
 * @param {function} events.onCancelEdit called when the user leaves edit mode
 * @param {boolean} [sendOnEnter]    whether onSend should be called on enter
 * @param {string} currentType       type of the current ticket/request
 * @param {number} currentStatus     status of the current ticket/request
 * @param {React.ReactNode[]} children the nested config children (the attachment upload subtree)
 * @returns {JSX.Element}
 */
// eslint-disable-next-line max-len
const MessageField = ({ forwardedRef, value = {}, group, events, sendOnEnter, currentType, currentStatus, edit: initialEdit = false, editLabel, children }) => {
  const theme = useTheme()
  const classes = useStyles(styles)()
  const { onOpen, onChange, onSend, onAttachment, onCancelEdit } = events
  const [, startTransition] = useTransition()
  const { eventBus } = useContext(ApplicationContext)

  const inputRef = useRef(null)
  const chipRef = useRef(null)
  const uploadRef = useRef(null)

  const uploadChild = useMemo(() => arrayOf(children || []).map(child => (child.key === 'upload'
    ? cloneElement(child, { ...child.props, group, menuRef: uploadRef })
    : null
  )), [children, group])

  const [attachmentCount, setAttachmentCount] = useState(0)
  const [options, setOptions] = useState([{}])
  const [hasSelected, setHasSelected] = useState(false)
  const [optionsOpen, setOptionsOpen] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const currentOptions = await onOpen(new PlatformEvent('open', { status: currentStatus, type: currentType }))
        currentOptions?.length && setOptions(currentOptions)
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  if (group) {
    // dispatched from adapter, once data or cache have been updated
    const _eventName = useRef(eventBus.type(G.ATTACHMENT, G.DONE, group))
    const _eventHandler = useEventCallback(({ detail }) => {
      setAttachmentCount(detail[G.DATA].length)
    })
    useEventHandler(eventBus, _eventName.current, _eventHandler)
  }

  const defaultOption = options.find(option => option.default)
  const initialOption = options && (defaultOption || options[0])
  const selectionRequired = options.some(e => e.priorSelection)
  const [textValue, setTextValue] = useState(
    initialOption?.proposedText
      ? initialOption.proposedText
      : value.text || '',
  )
  const [selectedOption, setSelectedOption] = useState(initialOption)
  const [editMode, setEditMode] = useState(initialEdit)

  useEffect(() => { value.text && setTextValue(value.text) }, [value])
  useEffect(() => { setSelectedOption(initialOption) }, [options])
  useEffect(() => { setEditMode(initialEdit) }, [initialEdit])
  useEffect(() => { editMode && setTextValue(value.text || '') }, [editMode])

  useEffect(() => {
    selectionRequired && !hasSelected && setOptionsOpen(true)
  }, [selectionRequired, hasSelected])

  const clear = useEventCallback((event) => {
    const empty = ''
    setTextValue(empty)
    onChange?.(new PlatformEvent(event, { value: empty }))
    inputRef.current?.focus()
  })

  const handleMenuItemClick = option => (event) => {
    const newValue = option.proposedText || ''
    setSelectedOption(option)
    newValue && setTextValue(newValue)
    startTransition(() => onChange?.(new PlatformEvent(event, { value: newValue })))
    setHasSelected(true)
    setOptionsOpen(false)
  }

  const handleInputChange = (event) => {
    const rawValue = event.target.value
    const text = sendOnEnter ? rawValue.replace(/(\r\n|\n|\r)/gm, '') : rawValue.trim()
    setTextValue(rawValue)
    startTransition(() => onChange?.(new PlatformEvent(event, { text })))
  }

  const sendMessage = handler => (event) => {
    const newStatus = selectedOption?.newStatus
    const newStatusReason = selectedOption?.newStatusReason
    const type = selectedOption?.key
    // Trims all leading and trailing newlines in text messages
    const text = textValue.trim()
    startTransition(() => {
      handler?.(new PlatformEvent(event, { newStatus, newStatusReason, text, type }))
    })
    setTextValue('')
    setEditMode(false)
  }

  const handleAttachmentPaste = (event) => {
    if (event.clipboardData?.files?.length) {
      onAttachment?.(new PlatformEvent(event, { files: event.clipboardData.files }))
    }
  }

  const handleChipClick = () => setOptionsOpen(prev => !prev)
  const handleMenuClose = () => setOptionsOpen(false)

  const handleSend = useEventCallback(event => sendMessage(onSend)(event))
  const handleUpload = useEventCallback(() => uploadRef.current?.present?.())

  const handleCancelEdit = useEventCallback((event) => {
    setEditMode(false)
    setTextValue('')
    onCancelEdit?.(new PlatformEvent(event))
  })

  const isDisabled = useMemo(
    () => !textValue && attachmentCount === 0,
    [textValue, attachmentCount],
  )
  const editPreview = useMemo(() => {
    const text = value?.text || ''
    return text.length > 50 ? `${text.slice(0, 50)}...` : text
  }, [value?.text])
  const showInput = !selectionRequired || hasSelected

  return (
    <Box
      ref={forwardedRef}
      className={classes.root}
      onPaste={handleAttachmentPaste}
    >
      {editMode && (
        <Box className={classes.editBanner}>
          <Box className={classes.editBannerLeft}>
            <Icon
              name={'edit'}
              size={'1.25rem'}
              color={'black.main'}
            />
            <Text
              value={`${editLabel || ''}${editPreview?.length ? ':' : ''}`}
              variant={'14/medium'}
              color={'black.main'}
            />
            <Text
              value={editPreview}
              variant={'14/regular'}
              color={'text.secondary'}
              noWrap
              maxLines={1}
              className={classes.editPreview}
            />
          </Box>
          <Box
            className={classes.editCancel}
            onClick={handleCancelEdit}
          >
            <Icon
              name={'clear'}
              size={'1.25rem'}
              color={'black.main'}
            />
          </Box>
        </Box>
      )}
      <Box
        className={classes.attachments}
        style={
          attachmentCount
            ? { marginBottom: '0.75rem', marginTop: '0.5rem' }
            : {}
        }
      >
        {uploadChild}
      </Box>

      {showInput && (
        <Box className={classes.inputContainer}>
          <InputBase
            inputRef={inputRef}
            fullWidth
            multiline
            value={textValue}
            placeholder={selectedOption?.backgroundText}
            classes={{ root: classes.field, input: classes.input }}
            onChange={handleInputChange}
            onKeyUp={event => sendOnEnter && event.key === 'Enter' && textValue
              && sendMessage(onSend)(event)}
            autoFocus={true}
          />
          {!!textValue && (
            <Box
              className={classes.clear}
              onClick={clear}
            >
              <Icon
                name={'clear'}
                size={'1.25rem'}
                color={'black.main'}
              />
            </Box>
          )}
        </Box>
      )}

      <Box className={classes.actions}>
        <Box className={classes.left}>
          {selectedOption?.key && (
            <Box
              ref={chipRef}
              className={classes.chipWrapper}
            >
              <Chip
                chevron
                size={'small'}
                shape={'rounded'}
                iconSize={'1.25rem'}
                chevronSize={'1rem'}
                chevronIcon={optionsOpen ? 'chevron-up' : 'chevron-down'}
                color={'black.main'}
                textVariant={'12/medium'}
                icon={selectedOption.icon}
                value={selectedOption.value}
                iconColor={selectedOption.color}
                backgroundColor={selectedOption.backgroundColor}
                events={{ onClick: handleChipClick }}
              />
            </Box>
          )}
          <FabButton
            size={'small'}
            icon={'paperclip'}
            color={'black.main'}
            background={'gray.950'}
            iconProps={{
              padding: 2,
            }}
            events={{ onClick: handleUpload }}
          />
        </Box>
        <FabButton
          icon={'send'}
          size={'small'}
          color={isDisabled ? 'gray.500' : 'white.main'}
          background={isDisabled ? 'gray.930' : 'primary.main'}
          events={{ onClick: handleSend }}
        />
      </Box>

      <Menu
        anchorEl={chipRef.current}
        open={optionsOpen && Boolean(chipRef.current)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { className: classes.menuPaper } }}
        MenuListProps={{ className: classes.menuList }}
      >
        {options.map((option, i) => (option.hidden ? null : (
          <MenuItem
            key={option.key || i}
            selected={option === selectedOption}
            onClick={handleMenuItemClick(option)}
            className={classes.menuItem}
          >
            {option.icon && (
              <Icon
                source={option.icon}
                color={option.color}
                background={option.backgroundColor}
                shape={'circle'}
                size={'1.25rem'}
                padding={'0.5rem'}
              />
            )}
            <Text
              value={option.labelText}
              variant={'14/medium'}
              color={'black.main'}
            />
          </MenuItem>
        )))}
      </Menu>
    </Box>
  )
}

export default useMemoRef(
  MessageField,
  props => [props.value, props.edit, props.types, props.children],
)
